import { store } from '../../state/store';
import { webchatApi } from '../api';
import type { TurnState, LlmMessage } from '../../types';
import { schemaToFields } from '../../utils/form-renderer';
import { isAutoScrollEnabled, scrollToBottom } from '../../utils/scroll';
import { generateId } from '../../components/inputBar/helpers';
import { ChatOrchestratorBase, type Constructor, CHAT_TIMEOUT_MS } from './orchestrator-base';

/**
 * The dangerous-tool confirmation flow: showing the confirmation form, resolving
 * the user's approve/deny choice, and resuming the stream once approved.
 *
 * IMPORTANT — composition order: this mixin calls `this.clearTurn` and
 * `this.handleTurnTimeout`, whose real implementations live in turn-handle.ts.
 * Those are declared below only as type-satisfying stubs; at runtime the real
 * methods must win, which requires `withTurnHandling` to wrap this mixin's
 * result (i.e. be applied AFTER/OUTSIDE `withConfirmationHandling` in
 * orchestrator.ts's composition) — JS always dispatches to the most-derived
 * override. See orchestrator.ts for the actual composition order.
 */
export function withConfirmationHandling<TBase extends Constructor<ChatOrchestratorBase>>(Base: TBase) {
  return class extends Base {
    protected showConfirmationForm(
      messageId: string,
      token: string,
      toolName: string,
      toolArgs: Record<string, unknown>,
      ttlMs: number,
    ): void {
      const toolDef = store.getState().tools.find(t2 => t2.name === toolName);
      const schema = toolDef?.inputSchema || {};
      const fields = schemaToFields(schema, toolArgs);
      const toolDescription = toolDef?.description;
      const formEnabled = toolDef?.formEnabled ?? true;

      store.setPendingConfirmation({ token, toolName, toolDescription, toolArgs, fields, messageId, formEnabled });

      store.updateMessage(messageId, { isStreaming: false });
      store.setState({ isLoading: false });

      const scrollContainer = this.shadow.querySelector('.panel-body') as HTMLDivElement | null;
      if (isAutoScrollEnabled(scrollContainer)) {
        scrollToBottom(scrollContainer);
      }

      if (this.confirmationTimeout) clearTimeout(this.confirmationTimeout);
      this.confirmationTimeout = setTimeout(() => {
        store.setPendingConfirmation(null);
        this.confirmationTimeout = null;
      }, ttlMs);
    }

    async resolveConfirmation(approved: boolean, toolArgs?: Record<string, unknown>): Promise<void> {
      const pc = store.getState().pendingConfirmation;
      if (!pc) return;

      if (this.confirmationTimeout) {
        clearTimeout(this.confirmationTimeout);
        this.confirmationTimeout = null;
      }

      if (approved) {
        this.approvedTools.add(pc.toolName);
      }

      store.setPendingConfirmation(null);
      await this.handleConfirmation(pc.messageId, pc.token, approved, toolArgs);
    }

    private async handleConfirmation(messageId: string, token: string, approved: boolean, toolArgs?: Record<string, unknown>): Promise<void> {
      try {
        const response = await webchatApi.confirmToolCall(token, approved, toolArgs);
        if (!response.ok) {
          console.error('[mcp-chat-widget] Confirmation failed:', response.status);
          return;
        }

        // If approved, resume streaming into the same message bubble that showed the confirmation,
        // so the response text and tool indicator appear together (consistent with auto-approved flow).
        if (approved) {
          const turnId = generateId();
          const turn: TurnState = {
            turnId,
            sourceConversationId: store.getState().conversationId,
            resolvedConversationId: store.getState().conversationId,
            clientChatId: store.getState().clientChatId,
            assistantId: messageId,
            accumulatedText: '',
            streamFlushHandle: null,
            abortController: new AbortController(),
            timeoutHandle: setTimeout(() => this.handleTurnTimeout(turnId), CHAT_TIMEOUT_MS),
            interrupted: false,
            abortReason: null,
            clientRoundTripCount: 0,
            failedNonRetryableCalls: new Set<string>(),
            succeededCallResults: new Map<string, string>(),
          };
          this.turns.set(turnId, turn);

          // A confirmation can only be approved from the widget's live UI - resume
          // directly onto boundTurnId (unlike sendMessage, there's no separate
          // "still pending in another conversation" case to preserve here).
          store.updateMessage(messageId, { isStreaming: true });
          store.setState({ isLoading: true, boundTurnId: turnId });

          try {
            await this.readChatStream(response, turn);
          } finally {
            this.clearTurn(turnId);
          }
        }
      } catch (err) {
        console.error('[mcp-chat-widget] Confirmation error:', err);
      }
    }

    // Implemented in turn-handle.ts — stubs only so this mixin's TS types check;
    // see the composition-order note above the file docblock.
    protected clearTurn(_turnId: string): void {
      throw new Error('clearTurn must be provided by withTurnHandling');
    }

    protected handleTurnTimeout(_turnId: string): void {
      throw new Error('handleTurnTimeout must be provided by withTurnHandling');
    }

    // Implemented in orchestrator.ts — the stream reader stays with the
    // top-level orchestration flow (sendMessage/readChatStream), which is
    // always the most-derived class, so this stub is always safely overridden.
    protected readChatStream(_response: Response | null, _turn: TurnState, _conversationMessages?: LlmMessage[], _pollRoundTrip?: number): Promise<void> {
      throw new Error('readChatStream must be provided by ChatOrchestrator');
    }
  };
}
